import { useState, useEffect } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, DatePicker, InputNumber, message, Tag, Statistic, Row, Col, Popconfirm } from 'antd'
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import { receivablesApi } from '../services/api'

const { Option } = Select
const { } = DatePicker

interface Receivable {
  id: number
  order_id: number
  order_no: string
  customer_id: number
  customer_name: string
  customer_type: number
  amount: number
  paid_amount: number
  remaining_amount: number
  status: number
  due_date: string
  remark: string
  created_at: string
}

const statusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待收款', color: 'orange' },
  1: { text: '部分收款', color: 'blue' },
  2: { text: '已结清', color: 'green' }
}

const customerTypeMap: Record<number, string> = {
  1: '个人客户',
  2: '企业客户'
}

export default function Receivables() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Receivable[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [payVisible, setPayVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentReceivable, setCurrentReceivable] = useState<Receivable | null>(null)
  const [payForm] = Form.useForm()
  const [searchForm] = Form.useForm()

  // 统计数据
  const [stats, setStats] = useState({ totalAmount: 0, paidAmount: 0, remainingAmount: 0 })

  useEffect(() => {
    loadData()
  }, [page, pageSize])

  const loadData = async () => {
    setLoading(true)
    try {
      const values = searchForm.getFieldsValue()
      const res = await receivablesApi.getList({
        page,
        page_size: pageSize,
        ...values
      })
      if (res.code === 200) {
        const list = (res.data?.list || []).map((item: any) => ({
          ...item,
          paid_amount: item.received_amount, // 后端 received_amount → 前端 paid_amount
          remaining_amount: Number(item.amount || 0) - Number(item.received_amount || 0)
        }))
        setData(list)
        setTotal(res.data?.total || 0)

        // 计算统计（MySQL返回DECIMAL为字符串，必须Number()转换）
        const totalAmount = list.reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0)
        const paidAmount = list.reduce((sum: number, item: any) => sum + Number(item.received_amount || 0), 0)
        setStats({ totalAmount, paidAmount, remainingAmount: totalAmount - paidAmount })
      }
    } catch (err) {
      message.error('加载应收款列表失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(1)
    loadData()
  }

  const handleReset = () => {
    searchForm.resetFields()
    setPage(1)
    loadData()
  }

  const handleDetail = (record: any) => {
    setCurrentReceivable({
      ...record,
      paid_amount: record.received_amount,
      remaining_amount: Number(record.amount || 0) - Number(record.received_amount || 0)
    })
    setDetailVisible(true)
  }

  const handlePay = (record: Receivable) => {
    setCurrentReceivable(record)
    payForm.resetFields()
    payForm.setFieldsValue({ amount: record.remaining_amount })
    setPayVisible(true)
  }

  const handlePaySubmit = async () => {
    try {
      const values = await payForm.validateFields()
      const res = await receivablesApi.pay(currentReceivable!.id, values)
      if (res.code === 200) {
        message.success('收款成功')
        setPayVisible(false)
        loadData()
      } else {
        message.error(res.message || '收款失败')
      }
    } catch (err) {
      message.error('收款失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const res = await receivablesApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        loadData()
      } else {
        message.error(res.message || '删除失败')
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  const columns = [
    { title: '应收款ID', dataIndex: 'id', key: 'id', width: 100 },
    { title: '订单号', dataIndex: 'order_no', key: 'order_no', width: 150 },
    { title: '客户名称', dataIndex: 'customer_name', key: 'customer_name', width: 120 },
    {
      title: '客户类型',
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 100,
      render: (v: number) => <Tag color={v === 2 ? 'blue' : 'default'}>{customerTypeMap[v] || '未知'}</Tag>
    },
    { title: '应收金额', dataIndex: 'amount', key: 'amount', width: 120, render: (v: number) => `¥${Number(v || 0).toFixed(2)}` },
    { title: '已收金额', dataIndex: 'paid_amount', key: 'paid_amount', width: 120, render: (v: number) => `¥${Number(v || 0).toFixed(2)}` },
    { title: '剩余金额', dataIndex: 'remaining_amount', key: 'remaining_amount', width: 120, render: (v: number) => <span style={{ color: v > 0 ? '#ff4d4f' : '#52c41a' }}>¥{Number(v || 0).toFixed(2)}</span> },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: number) => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text}</Tag>
    },
    { title: '到期日', dataIndex: 'due_date', key: 'due_date', width: 110, render: (v: string) => v?.substring(0, 10) },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: Receivable) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleDetail(record)}>详情</Button>
          {record.status !== 2 && (
            <Button type="link" size="small" onClick={() => handlePay(record)}>收款</Button>
          )}
          {record.status === 0 && (
            <Popconfirm title="确认删除该应收款记录?" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic title="应收总额" value={stats.totalAmount} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="已收金额" value={stats.paidAmount} precision={2} prefix="¥" valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="待收金额" value={stats.remainingAmount} precision={2} prefix="¥" valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="order_no">
            <Input placeholder="订单号" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="customer_name">
            <Input placeholder="客户名称" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" style={{ width: 120 }} allowClear>
              {Object.entries(statusMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="customer_type">
            <Select placeholder="客户类型" style={{ width: 120 }} allowClear>
              {Object.entries(customerTypeMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="应收款详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={500}
      >
        {currentReceivable && (
          <div>
            <p><strong>应收款ID：</strong>{currentReceivable.id}</p>
            <p><strong>订单号：</strong>{currentReceivable.order_no}</p>
            <p><strong>客户名称：</strong>{currentReceivable.customer_name}</p>
            <p><strong>客户类型：</strong>{customerTypeMap[currentReceivable.customer_type]}</p>
            <p><strong>应收金额：</strong>¥{Number(currentReceivable.amount || 0).toFixed(2)}</p>
            <p><strong>已收金额：</strong>¥{Number(currentReceivable.paid_amount || 0).toFixed(2)}</p>
            <p><strong>剩余金额：</strong>¥{Number(currentReceivable.remaining_amount || 0).toFixed(2)}</p>
            <p><strong>状态：</strong><Tag color={statusMap[currentReceivable.status]?.color}>{statusMap[currentReceivable.status]?.text}</Tag></p>
            <p><strong>到期日：</strong>{currentReceivable.due_date?.substring(0, 10) || '-'}</p>
            <p><strong>备注：</strong>{currentReceivable.remark || '-'}</p>
          </div>
        )}
      </Modal>

      {/* 收款弹窗 */}
      <Modal
        title="确认收款"
        open={payVisible}
        onOk={handlePaySubmit}
        onCancel={() => setPayVisible(false)}
      >
        <Form form={payForm} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          <Form.Item label="应收金额">
            <span style={{ fontSize: 18, fontWeight: 'bold' }}>¥{Number(currentReceivable?.amount || 0).toFixed(2)}</span>
          </Form.Item>
          <Form.Item label="已收金额">
            <span>¥{Number(currentReceivable?.paid_amount || 0).toFixed(2)}</span>
          </Form.Item>
          <Form.Item label="剩余金额">
            <span style={{ color: '#ff4d4f' }}>¥{Number(currentReceivable?.remaining_amount || 0).toFixed(2)}</span>
          </Form.Item>
          <Form.Item name="amount" label="本次收款" rules={[{ required: true, message: '请输入收款金额' }]}>
            <InputNumber min={0.01} max={currentReceivable?.remaining_amount} precision={2} style={{ width: '100%' }} prefix="¥" />
          </Form.Item>
          <Form.Item name="payment_method" label="支付方式">
            <Select placeholder="选择支付方式">
              <Option value="cash">现金</Option>
              <Option value="wechat">微信</Option>
              <Option value="alipay">支付宝</Option>
              <Option value="bank">银行转账</Option>
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
